import { EnhancedMarkdown } from '@/components/ui/EnhancedMarkdown'
import { Check, Copy, ExternalLink } from 'lucide-react'
import { ReactNode, useState } from 'react'
import type { WebPublisherResult } from 'sprites-types/workflow/tools/webPublisher'
import { FramerCMSItemDisplay } from '../Framer/FramerCMSItemDisplay'
import { FramerCollectionDisplay } from '../Framer/FramerCollectionDisplay'
import { FramerObjectDisplay, isJsonString, safeJsonParse } from '../Framer/FramerObjectDisplay'
import { ContextMenuWrapper } from './ContextMenuWrapper'

interface BaseWebPublisherDisplayProps {
  data: WebPublisherResult
  platformName: string
  platformIcon: ReactNode
  platformColor: string
  backgroundColor: string
  actionName?: string
  children?: ReactNode
}

async function copyToClipboard(text: string) {
  await navigator.clipboard.writeText(text)
}

export function BaseWebPublisherDisplay({
  data,
  platformName,
  platformIcon,
  platformColor,
  backgroundColor,
  actionName,
  children,
}: BaseWebPublisherDisplayProps) {
  const [showDetails, setShowDetails] = useState(false)

  // Check if there's any meaningful data to display
  const hasSummary = Boolean(data.summary && data.summary.trim())
  const hasUrl = Boolean(data.url)
  const hasChanges = Boolean(data.changes && data.changes.length > 0)
  const hasChildren = Boolean(children)

  // If there's no meaningful content, return null to avoid empty spaces
  if (!hasSummary && !hasUrl && !hasChanges && !hasChildren) {
    return null
  }

  // Extract URLs from various result fields for context menu
  const urls = [
    data.url,
    // Extract URLs from change details if they exist
    ...(data.changes
      ?.map((change) => {
        const urlMatch = change.details.match(/https?:\/\/[^\s]+/g)
        return urlMatch || []
      })
      .flat() || []),
  ].filter((url): url is string => Boolean(url))

  const contextActions = [
    {
      id: 'copy-summary',
      label: 'Copy Summary',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(data.summary)
      },
    },
    ...(urls.length > 0
      ? [
          {
            id: 'copy-url',
            label: 'Copy URL',
            icon: <Copy className="h-4 w-4" />,
            onClick: async () => {
              if (data.url) {
                await copyToClipboard(data.url)
              }
            },
          },
        ]
      : []),
  ]

  return (
    <ContextMenuWrapper actions={contextActions}>
      <div className="rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm">
        <div className="flex items-start gap-3 px-4 py-3">
          <div className="mt-0.5 flex-shrink-0">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-lg ${backgroundColor}`}
            >
              {platformIcon}
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium ${platformColor}`}>
                {actionName || `${platformName} Web Publisher`}
              </span>
              <Check className="h-4 w-4 text-green-600" />
            </div>

            {data.summary && (
              <div className="mt-2 rounded-lg bg-gray-50 p-3">
                <EnhancedMarkdown
                  className="prose prose-sm max-w-none text-gray-700"
                  style={{ backgroundColor: 'transparent' }}
                >
                  {data.summary}
                </EnhancedMarkdown>
              </div>
            )}

            {data.url && (
              <div className="mt-2 flex items-center gap-2 text-sm">
                <ExternalLink className="h-4 w-4 text-blue-500" />
                <a
                  href={data.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  View Content
                </a>
              </div>
            )}

            {data.changes && data.changes.length > 0 && (
              <div className="mt-3 space-y-2">
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900"
                >
                  Changes ({data.changes.length})
                  <span
                    className={`transform transition-transform ${showDetails ? 'rotate-90' : ''}`}
                  >
                    ▶
                  </span>
                </button>

                {showDetails && (
                  <div className="space-y-3">
                    {data.changes.map((change, index) => (
                      <EnhancedChangeDisplay
                        key={index}
                        change={change}
                        platformName={platformName}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {children}
          </div>
        </div>
      </div>
    </ContextMenuWrapper>
  )
}

interface EnhancedChangeDisplayProps {
  change: {
    action: string
    target: string
    details: string
  }
  platformName: string
}

/**
 * Enhanced change display that intelligently renders JSON objects and structured data
 */
function EnhancedChangeDisplay({ change, platformName }: EnhancedChangeDisplayProps) {
  // Check if details contains JSON data
  const isStructuredData = isJsonString(change.details)

  if (isStructuredData) {
    const parsedData = safeJsonParse(change.details)

    // Special handling for Framer platform
    if (platformName === 'Framer' && parsedData) {
      // Detect if this looks like collections data
      if (
        Array.isArray(parsedData) &&
        parsedData.length > 0 &&
        parsedData[0]?.name &&
        parsedData[0]?.fields
      ) {
        return (
          <div className="border-l-2 border-gray-200 pl-4">
            <div className="mb-2 flex items-center gap-2 font-medium text-gray-900">
              <span className="capitalize">{change.action}</span>
              <span className="text-gray-600">•</span>
              <span className="text-gray-600">{change.target}</span>
            </div>
            <FramerCollectionDisplay
              collections={parsedData}
              title="Collections Found"
              showDetails={true}
            />
          </div>
        )
      }

      // Detect if this looks like CMS items
      if (Array.isArray(parsedData) && parsedData.length > 0 && parsedData[0]?.fieldData) {
        return (
          <div className="border-l-2 border-gray-200 pl-4">
            <div className="mb-2 flex items-center gap-2 font-medium text-gray-900">
              <span className="capitalize">{change.action}</span>
              <span className="text-gray-600">•</span>
              <span className="text-gray-600">{change.target}</span>
            </div>
            <FramerCMSItemDisplay items={parsedData} title="CMS Items" showFullData={false} />
          </div>
        )
      }

      // Detect if this is a single CMS item
      if (parsedData?.fieldData) {
        return (
          <div className="border-l-2 border-gray-200 pl-4">
            <div className="mb-2 flex items-center gap-2 font-medium text-gray-900">
              <span className="capitalize">{change.action}</span>
              <span className="text-gray-600">•</span>
              <span className="text-gray-600">{change.target}</span>
            </div>
            <FramerCMSItemDisplay items={[parsedData]} title="CMS Item" showFullData={false} />
          </div>
        )
      }
    }

    // Generic structured data display
    return (
      <div className="border-l-2 border-gray-200 pl-4">
        <div className="mb-2 flex items-center gap-2 font-medium text-gray-900">
          <span className="capitalize">{change.action}</span>
          <span className="text-gray-600">•</span>
          <span className="text-gray-600">{change.target}</span>
        </div>
        <FramerObjectDisplay
          title="Data"
          data={parsedData}
          defaultExpanded={false}
          showExpandButton={true}
        />
      </div>
    )
  }

  // Fallback to original display for non-JSON content
  return (
    <div className="border-l-2 border-gray-200 pl-4">
      <div className="flex items-center gap-2 font-medium text-gray-900">
        <span className="capitalize">{change.action}</span>
        <span className="text-gray-600">•</span>
        <span className="text-gray-600">{change.target}</span>
      </div>
      <div className="ml-2 mt-1 text-sm text-gray-700">{change.details}</div>
    </div>
  )
}
